import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const team = [
  {
    key: "yasunori",
    img: "https://storage.readdy-site.link/project_files/040ce02b-a44d-49ab-a839-d4ba85899210/39d58ce6-8bc4-42c4-98a1-04e7daf74eb7_Yasunori-Kawauchi.jpg?v=8bd9945324852426040b6288d8ea8b05",
    objectPosition: "center top",
  },
  {
    key: "kevin",
    img: "https://public.readdy.ai/ai/img_res/edited_6a710bc11a345d3c366c79181234238f_acf292d4.jpg",
    objectPosition: "center top",
  },
];

const values = [
  { key: "integrity", icon: "ri-shield-check-line" },
  { key: "expertise", icon: "ri-lightbulb-line" },
  { key: "global", icon: "ri-global-line" },
  { key: "results", icon: "ri-focus-3-line" },
];

export default function AboutPage() {
  const { t } = useTranslation(["pages"]);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/91af6afbb3a03e65b743fe0295e1f35e.jpg"
            alt="About"
            className="w-full h-full object-cover object-top opacity-25"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-400 text-sm font-semibold uppercase tracking-widest mb-4">
            {t("pages:about.hero.subtitle")}
          </p>
          <h1
            className="text-5xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("pages:about.hero.title")}
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl mx-auto leading-relaxed">
            {t("pages:about.hero.description")}
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-emerald-700 text-sm font-semibold uppercase tracking-widest mb-4">
              {t("pages:about.story.subtitle")}
            </p>
            <h2
              className="text-4xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("pages:about.story.title")}
            </h2>
            {t("pages:about.story.paragraphs", { returnObjects: true }).map(
              (paragraph: string, idx: number) => (
                <p key={idx} className="text-slate-600 leading-relaxed mb-4">
                  {paragraph}
                </p>
              )
            )}
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold px-8 py-3 rounded-md transition-colors whitespace-nowrap cursor-pointer"
            >
              {t("pages:about.story.cta")} <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="w-full h-[460px] rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20energy%20consulting%20team%20in%20modern%20office%20boardroom%2C%20executives%20reviewing%20energy%20market%20strategy%20on%20large%20display%20screens%2C%20collaborative%20business%20meeting%2C%20natural%20light&width=800&height=600&seq=about002&orientation=landscape"
              alt="Medigreen Office"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-700 text-sm font-semibold uppercase tracking-widest mb-3">
              {t("pages:about.values.subtitle")}
            </p>
            <h2
              className="text-4xl font-bold text-slate-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("pages:about.values.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <div key={v.key} className="bg-white rounded-xl p-8 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 mx-auto mb-4">
                  <i className={`${v.icon} text-xl`} />
                </div>
                <h3 className="font-bold text-slate-900 mb-2">
                  {t(`pages:about.values.items.${v.key}.title`)}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {t(`pages:about.values.items.${v.key}.desc`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-700 text-sm font-semibold uppercase tracking-widest mb-3">
              {t("pages:about.company.subtitle")}
            </p>
            <h2
              className="text-4xl font-bold text-slate-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("pages:about.company.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                icon: "ri-map-pin-line",
                label: t("pages:about.company.address"),
                value: "1411 PALE SAN VITORES ROAD, STE 303,\nTAMUNING, GU",
                href: null,
              },
              {
                icon: "ri-phone-line",
                label: t("pages:about.company.phone"),
                value: "+1.671.482.0104",
                href: "tel:+16714820104",
              },
              {
                icon: "ri-mail-line",
                label: t("pages:about.company.email"),
                value: "info@medigreen.energy",
                href: "mailto:info@medigreen.energy",
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex flex-col items-center text-center bg-slate-50 rounded-xl p-8"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-700 mb-4">
                  <i className={`${item.icon} text-xl`} />
                </div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                  {item.label}
                </p>
                {item.href ? (
                  <a
                    href={item.href}
                    className="text-slate-700 text-sm leading-relaxed whitespace-pre-line hover:text-emerald-700 transition-colors cursor-pointer"
                  >
                    {item.value}
                  </a>
                ) : (
                  <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">
                    {item.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-emerald-700 text-sm font-semibold uppercase tracking-widest mb-3">
              {t("pages:about.team.subtitle")}
            </p>
            <h2
              className="text-4xl font-bold text-slate-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t("pages:about.team.title")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12 max-w-2xl mx-auto">
            {team.map((m) => (
              <div key={m.key} className="text-center">
                <div className="w-40 h-40 rounded-full overflow-hidden mx-auto mb-5">
                  <img
                    src={m.img}
                    alt={t(`pages:about.team.members.${m.key}.name`)}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: m.objectPosition }}
                  />
                </div>
                <h3 className="font-bold text-slate-900 text-lg">
                  {t(`pages:about.team.members.${m.key}.name`)}
                </h3>
                <p className="text-emerald-700 text-sm font-semibold mb-3">
                  {t(`pages:about.team.members.${m.key}.title`)}
                </p>
                <p className="text-slate-500 text-sm leading-relaxed">
                  {t(`pages:about.team.members.${m.key}.bio`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}